import { Subject } from 'rxjs';
import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, TemplateRef, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { DynamicDataTableComponent } from '@components/dynamic-data-table/dynamic-data-table.component';
import { TableColumn, TableConfig } from '@components/data-table/data-table.component';
import { DropdownComponent, DropdownOption } from '@components/dropdown/dropdown.component';
import { ButtonComponent } from '@components/button/button.component';
import { WeatherTileLayerService, WeatherTileLayerViewModel } from '@services/weather-tile-layer.service';
import { DateFormatLangPipe } from '@app/utils/pipes/date-format-lang-pipe';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { Textarea } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { DatePickerComponent } from '@components/date-picker/date-picker.component';
import { finalize, takeUntil } from 'rxjs/operators';
import { buildFilterQuery } from '@app/utils/_msf';

@Component({
  selector: 'app-weather-logging',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    DynamicDataTableComponent,
    DropdownComponent,
    ButtonComponent,
    ButtonModule,
    TooltipModule,
    DialogModule,
    Textarea,
    ToastModule,
    DatePickerComponent
  ],
  providers: [MessageService, DateFormatLangPipe, DatePipe],
  templateUrl: './weather-logging.component.html',
  styleUrl: './weather-logging.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WeatherLoggingComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  @ViewChild('annotationTemplate', { static: false }) annotationTemplate!: TemplateRef<any>;
  @ViewChild('actionTemplate', { static: false }) actionTemplate!: TemplateRef<any>;

  loading = true;
  toggleFilter: boolean | undefined = undefined;
  filtersParams: object[] = [];
  defaultParams = { take: 10, offset: 0, filters: [] as object[] };
  tableConfig: TableConfig = {
    paginator: true,
    rows: 10,
    rowsPerPageOptions: [10, 25, 50, 100],
  };

  selectedType: number = 3;
  selectedIndicator: string | null = null;
  selectedDataSource: string = 'ERA';
  tempType: number = 3;
  tempIndicator: string | null = null;
  tempDataSource: string = 'ERA';
  tempCreateAtPeriod?: Date[] | null;
  tempLayerDatePeriod?: Date[] | null;

  indicatorOptions: DropdownOption[] = [];
  typeOptions: DropdownOption[] = [];
  dataSourceOptions: DropdownOption[] = [];

  tableColumns: TableColumn[] = [];

  showEditModal = false;
  editLayer: WeatherTileLayerViewModel | null = null;
  editAnnotation = '';
  downloadingIds = new Set<string>();
  downloadingModal = false;

  constructor(
    public service: WeatherTileLayerService,
    private detector: ChangeDetectorRef,
    private router: Router,
    private translate: TranslateService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    // Force default filter on every load (do not restore from storage)
    try {
      localStorage.removeItem('weather_data_processing');
    } catch (_) {}
    this.generateTypeOptions();
    this.generateIndicatorOptions();
    this.generateDataSourceOptions();
    this.setupTableColumns();
    this.translate.onLangChange?.subscribe(() => {
      this.generateDataSourceOptions();
      this.detector.markForCheck();
    });
    this.tempType = this.selectedType;
    this.tempIndicator = this.selectedIndicator;
    this.tempDataSource = this.selectedDataSource;
    // Set initial filters so first request uses default filter values
    this.filtersParams = buildFilterQuery(this.getFilterModelForQuery() as Record<string, Object>);
  }

  generateTypeOptions(): void {
    // Порядок и наименования как в погода ГИС: Норма, Отклонение, Погода
    this.typeOptions = [
      { label: this.translate.instant('NORMAL'), value: 1 },
      { label: this.translate.instant('DEVIATION'), value: 2 },
      { label: this.translate.instant('Weather'), value: 3 }
    ];
  }

  generateIndicatorOptions(): void {
    const indicators = [
      'FrostDanger', 'Fallout', 'Humidity', 'SolarRadiationInfluence',
      'TemperatureAverage', 'TemperatureMax', 'TemperatureMaxTotal',
      'TemperatureMin', 'TemperatureMinTotal', 'NormalizedFallout',
      'NormalizedHumidity', 'NormalizedSolarRadiationInfluence',
      'NormalizedTemperatureAverage', 'NormalizedTemperatureMax',
      'NormalizedTemperatureMaxTotal', 'NormalizedTemperatureMin',
      'NormalizedTemperatureMinTotal', 'DeviationTemperatureAverage',
      'DeviationTemperatureMax', 'DeviationTemperatureMaxTotal',
      'DeviationTemperatureMin', 'DeviationTemperatureMinTotal',
      'ARVI', 'EVI', 'GNDVI', 'MNDWI', 'NDMI', 'NDVI', 'ORVI', 'OSAVI'
    ];
    indicators.forEach(indicator => {
      this.indicatorOptions.push({ label: indicator, value: indicator });
    });
  }

  generateDataSourceOptions(): void {
    this.dataSourceOptions = [
      { label: this.translate.instant('ERA'), value: 'ERA' },
      { label: this.translate.instant('IBM'), value: 'IBM' },
      { label: this.translate.instant('UGM'), value: 'UGM' }
    ];
    this.detector.markForCheck();
  }

  setupTableColumns(): void {
    this.tableColumns = [
      {
        field: 'dataSource',
        header: 'Data source',
        type: 'badge',
        parseFunction: (row: WeatherTileLayerViewModel) => this.translate.instant(row.dataSource || '')
      },
      { field: 'layerType', header: 'Data type', type: 'badge' },
      { field: 'indicatorName', header: 'Indicator', type: 'badge' },
      { 
        field: 'layerDate', 
        header: 'Layer date', 
        type: 'date',
        parseFunction: (row: WeatherTileLayerViewModel) => {
          if (!row.layerDate) return '';
          const date = new Date(row.layerDate);
          if (row.isDateWithoutYear) {
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const day = date.getDate().toString().padStart(2, '0');
            return `${day}.${month}`;
          }
          return this.formatDate(row.layerDate);
        }
      },
      { field: 'createAt', header: 'Created at', type: 'date' },
      {
        field: 'fileSizeKb',
        header: 'File size (KB)',
        type: 'text',
        parseFunction: (row: WeatherTileLayerViewModel) =>
          row.fileSizeKb != null && row.fileSizeKb !== undefined ? `${row.fileSizeKb} KB` : '—'
      },
      { field: 'annotation', header: 'Annotation', type: 'custom' as const },
      { field: 'action', header: 'Action', type: 'custom', customTemplate: this.actionTemplate }
    ];
  }

  formatDate(date: string | Date): string {
    if (!date) return '';
    const d = typeof date === 'string' ? new Date(date) : date;
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    return `${day}.${month}.${year}`;
  }

  getAnnotationDisplay(row: WeatherTileLayerViewModel): string {
    const a = row?.annotation ?? '';
    if (typeof a !== 'string') return '';
    const maxLen = 50;
    return a.length > maxLen ? a.slice(0, maxLen) + '...' : a;
  }

  ngAfterViewInit(): void {
    if (this.annotationTemplate) {
      const annotationColumn = this.tableColumns.find(col => col.field === 'annotation');
      if (annotationColumn) {
        annotationColumn.customTemplate = this.annotationTemplate;
      }
    }
    if (this.actionTemplate) {
      const actionColumn = this.tableColumns.find(col => col.field === 'action');
      if (actionColumn) {
        actionColumn.customTemplate = this.actionTemplate;
        this.detector.detectChanges();
      }
    }
  }

  isDownloading(id: string | undefined | null): boolean {
    if (!id) return false;
    return this.downloadingIds.has(id);
  }

  /** Build filter model for buildFilterQuery (only defined values). */
  private getFilterModelForQuery(): Record<string, unknown> {
    const f: Record<string, unknown> = {};
    if (this.tempType !== null && this.tempType !== undefined) f['type'] = this.tempType;
    if (this.tempDataSource != null && this.tempDataSource !== '') f['dataSource'] = this.tempDataSource;
    if (this.tempIndicator != null && this.tempIndicator !== '') f['indicatorName'] = this.tempIndicator;
    if (this.tempCreateAtPeriod?.length === 2) {
      f['createAtFrom'] = this.tempCreateAtPeriod[0];
      f['createAtTo'] = this.tempCreateAtPeriod[1];
    }
    if (this.tempLayerDatePeriod?.length === 2) {
      f['layerDateFrom'] = this.tempLayerDatePeriod[0];
      f['layerDateTo'] = this.tempLayerDatePeriod[1];
    }
    return f;
  }

  openEditModal(layer: WeatherTileLayerViewModel): void {
    this.editLayer = layer;
    this.editAnnotation = layer.annotation ?? '';
    this.showEditModal = true;
    this.detector.detectChanges();
  }

  closeEditModal(): void {
    this.showEditModal = false;
    this.editLayer = null;
    this.editAnnotation = '';
    this.detector.detectChanges();
  }

  saveEditAnnotation(): void {
    if (!this.editLayer) return;
    this.service.updateWeatherTileLayerAnnotation(this.editLayer.id, this.editAnnotation).subscribe({
      next: () => {
        this.closeEditModal();
        this.toggleFilter = !this.toggleFilter;
        this.detector.detectChanges();
      },
      error: () => {
        this.detector.detectChanges();
      }
    });
  }

  downloadTif(layer: WeatherTileLayerViewModel, context: 'table' | 'modal' = 'table'): void {
    if (!layer) {
      return;
    }

    if (context === 'modal') {
      if (this.downloadingModal) return;
      this.downloadingModal = true;
    } else {
      if (this.isDownloading(layer.id)) return;
      this.downloadingIds.add(layer.id);
    }
    this.detector.detectChanges();

    this.service
      .downloadWeatherTileLayerTif(layer.id)
      .pipe(
        finalize(() => {
          if (context === 'modal') {
            this.downloadingModal = false;
          } else {
            this.downloadingIds.delete(layer.id);
          }
          this.detector.detectChanges();
        }), takeUntil(this.destroy$)
      )
      .subscribe({
        next: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.style.display = 'none';
          document.body.appendChild(link);

          const extension = blob.type && blob.type.includes('zip') ? 'zip' : 'tif';
          link.download = `${layer.geoServerName}.${extension}`;
          link.click();

          setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          }, 100);
        },
        error: (err) => {
          console.error('Error downloading TIF file:', err);
          const summary = this.translate.instant('Error');
          const detail = this.translate.instant('Download failed');
          this.messageService.add({ severity: 'error', summary, detail, life: 5000 });
        },
      });
  }

  applyFilters(): void {
    this.selectedType = this.tempType;
    this.selectedIndicator = this.tempIndicator;
    this.selectedDataSource = this.tempDataSource;
    this.filtersParams = buildFilterQuery(this.getFilterModelForQuery() as Record<string, Object>);
    this.detector.detectChanges();
    // Trigger reload in next tick so table receives updated [filter] before loadData()
    setTimeout(() => {
      this.toggleFilter = !this.toggleFilter;
      this.detector.detectChanges();
    }, 0);
  }

  resetFilters(): void {
    this.tempType = 3;
    this.tempIndicator = null;
    this.tempDataSource = 'ERA';
    this.tempCreateAtPeriod = null;
    this.tempLayerDatePeriod = null;
    this.selectedType = 3;
    this.selectedIndicator = null;
    this.selectedDataSource = 'ERA';
    // Build default filters so request uses them, not empty
    this.filtersParams = buildFilterQuery(this.getFilterModelForQuery() as Record<string, Object>);
    this.detector.detectChanges();
    // Trigger reload in next tick so table receives updated [filter] before loadData()
    setTimeout(() => {
      this.toggleFilter = !this.toggleFilter;
      this.detector.detectChanges();
    }, 0);
  }

  goBack(): void {
    this.router.navigate(['/weather/administration']);
  }

  exportToCsv(): void {
    const filters = this.getFilterModelForQuery() as Record<string, unknown>;
    this.service.getWeatherTileLayers(filters as any, 10000, 0).pipe(takeUntil(this.destroy$)).subscribe({
      next: (allLayers) => {
        const headers = ['Data source', 'Layer type', 'Indicator', 'Layer date', 'Created at', 'Annotation'];
        const rows = allLayers.map(layer => [
          layer.dataSource || '',
          layer.layerType || '',
          layer.indicatorName || '',
          layer.layerDate ? new Date(layer.layerDate.toString()).toLocaleDateString() : '',
          new Date(layer.createAt).toLocaleDateString(),
          layer.annotation ?? ''
        ]);

        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `weather-layers-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      },
      error: (err) => {
        console.error('Error exporting to CSV:', err);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
